import PropTypes from "prop-types";
import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import { throwUnlessIgnored } from "src/util/errorUtil";

const InfiniteContainer = styled.div`
  position: relative;
`;
const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InfiniteScroll = ({ hasMore, loadMore, loader, children, threshold }) => {
  const [isLoading, setIsLoading] = useState(false);
  const sentinelRef = useRef(null);
  // Use a ref to track loading state synchronously to prevent race conditions
  const loadingRef = useRef(false);

  const onLoadMore = useCallback(async () => {
    // Check ref synchronously to prevent concurrent calls
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    try {
      await loadMore();
    } catch (e) {
      throwUnlessIgnored(e);
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
    // isLoading is intentionally excluded from dependencies to prevent
    // unnecessary IntersectionObserver recreation on every loading state change.
    // The loadingRef prevents race conditions without causing observer recreation.
  }, [loadMore]);

  // Simple intersection observer for the sentinel element
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: threshold ? `${threshold}px` : "0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore, threshold]);

  return (
    <InfiniteContainer>
      {children}
      {hasMore && <div ref={sentinelRef} style={{ height: "1px" }} />}
      {isLoading && <LoaderContainer>{loader}</LoaderContainer>}
    </InfiniteContainer>
  );
};

InfiniteScroll.propTypes = {
  children: PropTypes.node,
  loader: PropTypes.node.isRequired,
  hasMore: PropTypes.bool.isRequired,
  loadMore: PropTypes.func.isRequired,
  threshold: PropTypes.number,
};

export default InfiniteScroll;
